import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row, Table, Dropdown, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons, SettingsIcons } from "../../assets/images";
import { useNavigate } from "react-router-dom";
// Redux Imports
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setShowClientExportDropdown,
  setClientListPageSize,
  setClientListPageNo,
  setFilterClientPageNo,
  setFilterClientPageSize,
} from "../../store/slices/clientsSlice";
import { setClientsChange } from "../../store/slices/commonSlice";
import { LoaderBlue } from "../Lotties";
// API Imports
import { useDeleteClientMutation } from "../../services/Clients";
import { Client } from "../../interfaces/project";
import { exportToCsv, exportToPDF } from "../../config";
import ClientsFilterModal, { FormProps } from "../Modals/client-filter";
import { useReactToPrint } from "react-to-print";
import SelectSearch from "react-select-search";

const AllClients = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => tableRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const state = useAppSelector((state) => state.clients);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<{
    [key: string]: boolean;
  }>({});
  const clientsChange = useAppSelector((state) => state.common.clientsChange);
  const clientIds = useAppSelector((state) => state.clients.clientIds);
  // Options
  const options = clientIds.map((item) => ({
    name: item.clientId + " - " + item.clientName,
    value: item.clientId,
  }));
  // Pagination && Filter
  const [filterInput, setFilterInput] = useState<FormProps>({
    city: "",
    country: "",
    clientStatus: "",
  });
  const [filterSearch, setFilterSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<Client[]>([]);
  const totalPages = Math.ceil(
    state.clientListTotalPages / state.clientListPageSize,
  );
  const totalCount = filterSearch
    ? state.filterClientTotalPages
    : state.clientListTotalPages;

  const filterTotalPages = Math.ceil(
    state.filterClientTotalPages / state.filterClientPageSize,
  );

  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage =
    Math.floor((state.clientListPageNo - 1) / showPages) * showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  const filterStartPage =
    Math.floor((state.filterClientPageNo - 1) / showPages) * showPages;
  const filterEndPage = filterStartPage + showPages;
  const filterPages = [...Array(filterTotalPages).keys()].slice(
    filterStartPage,
    filterEndPage,
  );
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.clientListPageNo > totalPages - showPages) {
      return;
    }
    dispatch(setClientListPageNo(state.clientListPageNo + showPages));
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.clientListPageNo <= 5) {
      return;
    }
    dispatch(setClientListPageNo(state.clientListPageNo - showPages));
  };

  const handleFilterGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.filterClientPageNo > filterTotalPages - showPages) {
      return;
    }
    dispatch(setFilterClientPageNo(state.filterClientPageNo + showPages));
  };

  const handleFilterGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.filterClientPageNo <= 5) {
      return;
    }
    dispatch(setFilterClientPageNo(state.filterClientPageNo - showPages));
  };

  const handlePageChange = (page: any) => {
    dispatch(setClientListPageNo(page));
  };

  const handleFilterPageChange = (page: any) => {
    dispatch(setFilterClientPageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    if (filterSearch) {
      dispatch(setFilterClientPageSize(selectedPageSize));
    } else {
      dispatch(setClientListPageSize(selectedPageSize));
    }
  };
  useEffect(() => {
    setFilteredData(state.clientList);
  }, [state.clientList]);

  // Employees List
  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Client Id.",
      "Client Name",
      "Contact Person",
      "Contact Person Designation",
      "Contact Person No.",
      "Company Name",
      "VAT Number",
      "Total Balance (SAR)",
      "Client Status",
      "Actions",
    ],
    tableData: filteredData,
  };

  const csvData = tableData.tableData.map((item, index) => ({
    "Sr. No.": index + 1,
    "Client Id.": item.clientId,
    "Client Name": item.clientName,
    "Contact Person": item.contactPersonName,
    "Contact Person Designation": item.contactPersonDesignation,
    "Contact Person No.": item.phone,
    "Company Name": item.companyName,
    "VAT Number": item.numberVAT,
    "Total Balance (SAR)": item.totalBalance,
    "Client Status": item.clientStatus,
  }));

  const [deleteClient] = useDeleteClientMutation();

  const handleDelete = async (empId: string) => {
    setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: true }));
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData?.permissions.deleteProject || false,
        payload: {
          clientId: empId,
        },
      };
      await deleteClient(data).unwrap();
      dispatch(setClientsChange(!clientsChange));
    } catch (e) {
      console.log(e);
    } finally {
      setDeleteLoadingMap((prevMap) => ({ ...prevMap, [empId]: false }));
    }
  };

  const handleReset = () => {
    setQuery("");
    setSearch("");
    setFilteredData(state.clientList);
    setFilterSearch(false);
    setFilterInput({
      city: "",
      country: "",
      clientStatus: "",
    });
  };

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <ClientsFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        setClients={setFilteredData}
        filterSearch={filterSearch}
        setFilterSearch={setFilterSearch}
        clients={state.clientList}
        query={query}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <h1 className="heading mb-0">All Clients</h1>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <div
                className=" d-flex align-items-center gap-2"
                style={{ width: "25%" }}
              >
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <SelectSearch
                      options={options}
                      onChange={(value: any) => {
                        setQuery(value);
                        setFilterSearch(true);
                      }}
                      value={query}
                      search={true}
                      placeholder="Search Client by Id/Name "
                    />
                    <img src={Common.ArrowDown} alt="message" />
                  </Form.Group>
                </Form.Group>
                {query !== "" ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : filterSearch ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-4">
                <button
                  className="dropdown-toggle-white dropdown-toggle-blue"
                  type="button"
                  onClick={() => navigate("/clients/add-client")}
                >
                  <img src={Common.Plus} height="22px" width="22px" alt="img" />{" "}
                  New
                </button>
                <button
                  id="dropdown-basic"
                  onClick={() => {
                    setShowFilterModal(true);
                  }}
                  className="dropdown-toggle-white dropdown-toggle-blue"
                >
                  Filter{" "}
                </button>
                <Dropdown
                  show={state.showClientExportDropdown}
                  className="position-relative"
                >
                  <button
                    id="dropdown-basic"
                    onClick={() => {
                      dispatch(
                        setShowClientExportDropdown(
                          !state.showClientExportDropdown,
                        ),
                      );
                    }}
                    className=" bg-transparent dropdown-toggle-white"
                  >
                    Export{" "}
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="ms-2"
                      alt="down-arrow"
                    />
                  </button>
                  <Dropdown.Menu
                    className="mt-1 custom-scrollbar"
                    show={state.showClientExportDropdown}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        exportToCsv(csvData, "clients-list");
                        dispatch(setShowClientExportDropdown(false));
                      }}
                    >
                      CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        dispatch(setShowClientExportDropdown(false));
                        handlePrinted();
                      }}
                    >
                      PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </Col>
        <Col sm="12">
          {state.clientListLoading ? (
            <LoaderBlue height={70} width={70} />
          ) : profileData.permissions.showProject ? (
            <>
              {tableData.tableData?.length > 0 ? (
                <div className="p-3" ref={tableRef}>
                  {print && (
                    <>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={Common.Vision} width="110px" alt="logo" />
                        </div>
                        <div className="letter-logo text-center gap-2">
                          <img src={Common.LogoSVG} width="80px" alt="logo" />
                          <div className="mt-1">
                            <h3
                              className="logo-text mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "10px",
                                fontWeight: "700",
                              }}
                            >
                              Senyar Arabian Trading Co. LTD
                            </h3>
                            <h3
                              className="logo-text logo-arabic mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              شركة سنيار العربية التجارية المحدودة
                            </h3>
                          </div>
                        </div>
                      </div>
                      <h1 className="sub-heading text-center mt-3">
                        Clients List
                      </h1>
                    </>
                  )}
                  <Table
                    striped
                    bordered
                    responsive
                    className={
                      print
                        ? "mt-5 text-center print-table"
                        : "mt-5 text-center"
                    }
                  >
                    <thead>
                      <tr className="border-0">
                        {tableData.tableHeading.map((item, index) => (
                          <th
                            className={
                              item === "Actions" && print
                                ? "border-0 d-none"
                                : "border-0"
                            }
                            key={index}
                          >
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.tableData.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderColor: "rgba(32, 32, 32, 0.20)" }}
                        >
                          <td>
                            {totalCount > 0
                              ? filterSearch
                                ? (state.filterClientPageNo - 1) *
                                    state.filterClientPageSize +
                                  index +
                                  1
                                : (state.clientListPageNo - 1) *
                                    state.clientListPageSize +
                                  index +
                                  1
                              : index + 1}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/clients/view-client/${item.clientId}`,
                                {
                                  state: { data: item },
                                },
                              )
                            }
                          >
                            {item.clientId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/clients/view-client/${item.clientId}`,
                                {
                                  state: { data: item },
                                },
                              )
                            }
                          >
                            {print
                              ? twoWords(item.clientName)
                              : item.clientName}
                          </td>
                          <td>
                            {print
                              ? twoWords(item.contactPersonName)
                              : item.contactPersonName}
                          </td>
                          <td>
                            {print
                              ? twoWords(item.contactPersonDesignation)
                              : item.contactPersonDesignation}
                          </td>
                          <td>{item.phone}</td>
                          <td>
                            {print
                              ? twoWords(item.companyName)
                              : item.companyName}
                          </td>
                          {/*<td>{item.city}</td>*/}
                          {/*<td>{item.country}</td>*/}
                          <td>{item.numberVAT}</td>
                          <td>{item.totalBalance?.toFixed(2)}</td>
                          <td>{item.clientStatus}</td>
                          {!print && (
                            <td>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/clients/view-client/${item.clientId}`,
                                    {
                                      state: { data: item },
                                    },
                                  )
                                }
                              >
                                <img
                                  src={Common.Eye}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/clients/edit-client/${item.clientId}`,
                                    {
                                      state: { data: item },
                                    },
                                  )
                                }
                              >
                                <img
                                  src={SettingsIcons.EditUser}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              {deleteLoadingMap[item.clientId] ? (
                                <LoaderBlue height={20} width={20} />
                              ) : (
                                <button
                                  className="btn p-0 border-0"
                                  onClick={() => handleDelete(item.clientId)}
                                >
                                  <img
                                    src={SettingsIcons.Delete}
                                    width="24px"
                                    height="24px"
                                    alt="edit-user"
                                  />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <h1 className="heading">No clients found</h1>
                </div>
              )}
            </>
          ) : (
            <div className="text-center mt-5">
              <h1 className="heading">Unauthorized Access</h1>
            </div>
          )}
        </Col>
        {filteredData.length > 0 && (
          <Col sm="12">
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <Form.Group
                className="d-flex align-items-center gap-2"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "14px", width: "230px" }}
                >
                  Entries per page
                </h4>
                <Form.Select
                  name="pageSize"
                  onChange={handlePageSizeChange}
                  value={
                    filterSearch
                      ? state.filterClientPageSize.toString()
                      : state.clientListPageSize.toString()
                  }
                  className="custom-input border-fill custom-height"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="100">100</option>
                  <option value="10000">All</option>
                </Form.Select>
              </Form.Group>
              <div className="pagination-custom">
                <Pagination className="justify-content-end">
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterGoToPreviousPages()
                        : handleGoToPreviousPages()
                    }
                  >
                    <img src={Common.DoubleArrowLeft} alt="arrow" />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterClientPageNo > 1
                              ? state.filterClientPageNo - 1
                              : 1,
                          )
                        : handlePageChange(
                            state.clientListPageNo > 1
                              ? state.clientListPageNo - 1
                              : 1,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-left"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  {filterSearch ? (
                    <>
                      {/* render filter pages here */}
                      {filterPages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handleFilterPageChange(page + 1)}
                          className={
                            page + 1 === state.filterClientPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* render pages here */}
                      {pages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handlePageChange(page + 1)}
                          className={
                            page + 1 === state.clientListPageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  )}
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterClientPageNo < filterTotalPages
                              ? state.filterClientPageNo + 1
                              : filterTotalPages,
                          )
                        : handlePageChange(
                            state.clientListPageNo < totalPages
                              ? state.clientListPageNo + 1
                              : totalPages,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-right"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      filterSearch
                        ? handleFilterGoToNextPages()
                        : handleGoToNextPages();
                    }}
                  >
                    <img src={Common.DoubleArrowRight} alt="arrow" />
                  </Pagination.Item>
                </Pagination>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
export default AllClients;
